import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ChevronRight, Send, Loader2, CheckCircle2 } from "lucide-react";
import api, { apiErrorMessage } from "../services/api";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Merci de renseigner votre nom, votre email et votre message.");
      return;
    }
    setLoading(true); setError("");
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError(apiErrorMessage(err, "Une erreur est survenue. Veuillez réessayer."));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <section className="hero-swoosh">
        <div className="max-w-[1000px] mx-auto px-5 pt-8 pb-8 relative z-10">
          <div className="flex items-center gap-1.5 text-[14px] text-slate-400 mb-4">
            <Link to="/" className="hover:text-[#FF3B3B]">Accueil</Link>
            <ChevronRight size={15} />
            <span className="text-slate-50 font-semibold">Nous contacter</span>
          </div>
          <h1 className="font-extrabold italic uppercase tracking-tight text-slate-50 text-3xl md:text-5xl mb-2 flex items-center gap-3">
            <Mail className="text-[#FF3B3B]" size={40} /> Nous contacter
          </h1>
          <p className="text-slate-400">Une question sur une pièce ou une commande ? Notre équipe vous répond.</p>
        </div>
      </section>

      <section className="max-w-[1000px] mx-auto px-5 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-[#10131A] rounded-2xl p-6 text-white space-y-4">
            <div className="font-bold text-lg">Coordonnées</div>
            <div className="flex items-center gap-3 text-[15px]"><Mail size={18} className="text-[#FF3B3B]" /> contact@ktsauto.fr</div>
            <div className="text-[13px] text-white/60 pt-2 border-t border-white/10">Nous répondons sous 24h ouvrées.</div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {sent ? (
            <div data-testid="contact-success" className="bg-emerald-950/40 border border-emerald-800/50 rounded-2xl p-10 text-center">
              <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-3" />
              <div className="font-extrabold italic uppercase tracking-tight text-slate-50 text-2xl mb-1">Message envoyé !</div>
              <p className="text-slate-300">Merci, nous vous répondrons dans les plus brefs délais.</p>
              <button data-testid="contact-new-btn" onClick={() => setSent(false)} className="btn-orange rounded-lg px-6 py-3 font-bold mt-5">Envoyer un autre message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-[#10131A] rounded-2xl border border-[#1E232D] p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Nom *</label>
                  <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50" placeholder="Votre nom" />
                </div>
                <div>
                  <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Email *</label>
                  <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50" placeholder="vous@email.fr" />
                </div>
              </div>
              <div>
                <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Sujet</label>
                <input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50" placeholder="Objet de votre demande" />
              </div>
              <div>
                <label className="block text-[14px] font-semibold text-slate-50 mb-1.5">Message *</label>
                <textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={6} className="w-full border-2 border-[#222735] rounded-lg px-3 py-3 outline-none focus:border-[#E01F1F] text-slate-50 resize-y" placeholder="Décrivez votre demande…" />
              </div>
              {error && <p data-testid="contact-error" className="text-red-400 text-[14px] font-medium">{error}</p>}
              <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-orange rounded-lg px-8 py-3.5 font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Envoyer le message
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
